import { API_BASE_URL, getAuthHeader } from './api'

export type Plan = 'free' | 'pro2' | 'pro5'

export interface BillingStatus {
  plan: Plan
  plan_status: string
  billing_provider?: string | null
  lifetime_free_used: boolean
  free_reports_remaining: number
  monthly_report_limit: number
  monthly_report_used: number
  single_report_credits: number
  reserved_free_report_count?: number
  reserved_monthly_report_count?: number
  reserved_single_report_count?: number
  available_free_report_count?: number
  available_monthly_report_count?: number
  available_single_report_credits?: number
  can_start: boolean
  current_period_end: number | null
}

export interface CheckoutSessionResponse {
  clientSecret: string
  sessionId: string
  publishableKey?: string
}

export const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''

interface ResolveCheckoutPublishableKeyOptions {
  runtimePublishableKey?: string | null
  buildTimePublishableKey?: string | null
  hostname?: string | null
}

export function resolveCheckoutPublishableKey({
  runtimePublishableKey,
  buildTimePublishableKey = STRIPE_PUBLISHABLE_KEY,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
}: ResolveCheckoutPublishableKeyOptions): {
  publishableKey: string
  source: 'runtime' | 'build-time-local' | 'missing-runtime'
} {
  if (runtimePublishableKey) {
    return { publishableKey: runtimePublishableKey, source: 'runtime' }
  }

  const normalizedHost = (hostname || '').toLowerCase()
  const isLocalHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1' || normalizedHost === '[::1]'
  if (isLocalHost && buildTimePublishableKey) {
    return { publishableKey: buildTimePublishableKey, source: 'build-time-local' }
  }

  return { publishableKey: '', source: 'missing-runtime' }
}

export const BILLING_ALERT_STATUSES = ['past_due', 'incomplete', 'incomplete_expired', 'unpaid']

export function hasBillingIssueStatus(status?: BillingStatus | null): boolean {
  if (!status) return false
  if (status.plan === 'free') return false
  const normalized = (status.plan_status || '').toLowerCase()
  return normalized === 'canceled' || BILLING_ALERT_STATUSES.includes(normalized)
}

export interface PlanPrices {
  single?: number
  pack2?: number
  pack3?: number
  pro2?: number
  pro5?: number
}

export async function getPrices(): Promise<PlanPrices> {
  const res = await fetch(`${API_BASE_URL}/api/billing/prices`)
  if (!res.ok) throw new Error(`Failed to get prices (${res.status})`)
  return res.json()
}

export async function getBillingStatus(): Promise<BillingStatus> {
  const res = await fetch(`${API_BASE_URL}/api/billing/status`, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
  })
  if (!res.ok) throw new Error(`Failed to get billing status (${res.status})`)
  return res.json()
}

export async function createStripeCustomer(email?: string, name?: string, country?: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/api/billing/create-customer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ email, name, country })
  })
  if (!res.ok) {
    let message = `Failed to create customer (${res.status})`
    try {
      const err = await res.json()
      if (typeof err?.detail === 'string') {
        message = err.detail
      } else if (err?.detail) {
        const detailMessage = err.detail.message || err.detail.code
        const hint = err.detail.hint
        message = [detailMessage, hint].filter(Boolean).join(' ').trim() || message
      } else if (err?.message) {
        message = err.message
      }
    } catch {
      // ignore JSON parse errors
    }
    throw new Error(message)
  }
  const data = await res.json()
  return data.customerId
}

export async function subscribe(plan: 'pro2' | 'pro5'): Promise<CheckoutSessionResponse> {
  const returnUrl = window.location.origin + '/#/profile'
  const res = await fetch(`${API_BASE_URL}/api/billing/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ plan, return_url: returnUrl })
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({} as any))
    const detail = typeof err?.detail === 'string'
      ? err.detail
      : (err?.detail?.message || err?.message || `Failed to subscribe (${res.status})`)
    throw new Error(detail)
  }
  const data = await res.json()
  return {
    clientSecret: data.clientSecret,
    sessionId: data.sessionId,
    publishableKey: data.publishableKey,
  }
}

export async function buySingleReport(
  quantity: number = 1,
  plan: 'single' | 'pack2' | 'pack3' = 'single',
): Promise<CheckoutSessionResponse> {
  const returnUrl = window.location.origin + '/#/profile'
  const res = await fetch(`${API_BASE_URL}/api/billing/buy-single-report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ return_url: returnUrl, quantity, plan })
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({} as any))
    const detail = typeof err?.detail === 'string'
      ? err.detail
      : (err?.detail?.message || err?.message || `Failed to purchase (${res.status})`)
    throw new Error(detail)
  }
  const data = await res.json()
  return {
    clientSecret: data.clientSecret,
    sessionId: data.sessionId,
    publishableKey: data.publishableKey,
  }
}

export async function confirmCheckout(sessionId: string): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/billing/confirm-checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ session_id: sessionId })
  })
  if (!res.ok) throw new Error('Failed to confirm checkout')
  return res.json()
}

export type BillingPortalFlowType = 'payment_method_update' | 'default'

export interface BillingPortalOptions {
  returnUrl?: string
  flowType?: BillingPortalFlowType
}

export async function getBillingPortal(options?: BillingPortalOptions): Promise<string> {
  const payload: Record<string, any> = {}
  if (options?.returnUrl) {
    payload.return_url = options.returnUrl
  }
  if (options?.flowType && options.flowType !== 'default') {
    payload.flow_type = options.flowType
  }
  const res = await fetch(`${API_BASE_URL}/api/billing/portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify(payload)
  })
  if (!res.ok) throw new Error(`Failed to get portal (${res.status})`)
  const data = await res.json()
  return data.url
}
